import { supabase } from "../supabase";

const PENDING_CODE_KEY = "precise-plate-pending-friend-code";

export type FriendAccessStatus = {
  fullAccess: boolean;
  source: "promo_code" | "owner" | "support" | "paid" | "free";
  accessCodeActive: boolean;
  planCode: "free" | "household";
  canSaveMeals: boolean;
  canUseCalendar: boolean;
  canManageHousehold: boolean;
  pantryLimit: number | null;
  pantryCount: number;
  generatorUsesLimit: number | null;
  generatorUsesUsed: number;
  generatorUsesRemaining: number | null;
  allowedPlanningModes: string[];
};

export type GenerationAttemptResult = {
  allowed: boolean;
  counted: boolean;
  reason: "household_required" | "monthly_limit" | null;
  remaining: number | null;
  attemptId: string | null;
};

// Discard codes saved by older releases; account access now comes from its plan.
export function clearRememberedFriendAccessCode(): void {
  try { window.localStorage.removeItem(PENDING_CODE_KEY); } catch { /* Storage may be disabled. */ }
}

export async function getFriendAccessStatus(): Promise<FriendAccessStatus> {
  const { data, error } = await supabase.rpc("get_my_access_status");
  if (error) throw error;
  const value = typeof data === "object" && data !== null ? data as Record<string, unknown> : {};
  const source = value.source === "promo_code" || value.source === "owner" || value.source === "support" || value.source === "paid"
    ? value.source
    : "free";
  const fullAccess = value.full_access === true;
  return {
    fullAccess,
    source,
    accessCodeActive: value.access_code_active === true,
    planCode: value.plan_code === "household" || fullAccess ? "household" : "free",
    canSaveMeals: value.can_save_meals === true || fullAccess,
    canUseCalendar: value.can_use_calendar === true || fullAccess,
    canManageHousehold: value.can_manage_household === true || fullAccess,
    pantryLimit: nullableNumber(value.pantry_limit),
    pantryCount: numberValue(value.pantry_count),
    generatorUsesLimit: nullableNumber(value.generator_uses_limit),
    generatorUsesUsed: numberValue(value.generator_uses_used),
    generatorUsesRemaining: nullableNumber(value.generator_uses_remaining),
    allowedPlanningModes: Array.isArray(value.allowed_planning_modes)
      ? value.allowed_planning_modes.filter((mode): mode is string => typeof mode === "string")
      : fullAccess ? ["single_meal", "family_single_meal", "single_day", "single_week", "family_day", "family_week"] : ["single_meal"],
  };
}

export async function setAdminPlanPreview(planCode: FriendAccessStatus["planCode"]): Promise<void> {
  const { error } = await supabase.rpc("set_my_admin_plan_preview", { p_plan_code: planCode });
  if (error) throw error;
}

export async function startGenerationAttempt(planningMode: string): Promise<GenerationAttemptResult> {
  const { data, error } = await supabase.rpc("start_generation_attempt", { p_planning_mode: planningMode });
  if (error) throw error;
  const value = typeof data === "object" && data !== null ? data as Record<string, unknown> : {};
  const reason = value.reason === "household_required" || value.reason === "monthly_limit" ? value.reason : null;
  return {
    allowed: value.allowed === true,
    counted: value.counted === true,
    reason,
    remaining: nullableNumber(value.remaining),
    attemptId: typeof value.attempt_id === "string" ? value.attempt_id : null,
  };
}

export async function completeGenerationAttempt(attemptId: string, succeeded: boolean): Promise<number | null> {
  const { data, error } = await supabase.rpc("complete_generation_attempt", {
    p_attempt_id: attemptId,
    p_succeeded: succeeded,
  });
  if (error) throw error;
  const value = typeof data === "object" && data !== null ? data as Record<string, unknown> : {};
  return nullableNumber(value.remaining);
}

function numberValue(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function nullableNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}
